import * as React from "react";
import {Box, Container, Divider, Heading, Link, Stack, Text} from "@chakra-ui/react";

const PrivacyPolicy: React.FC = () => {
    return (
        <Box minH={"100dvh"} maxH={"100dvh"} overflowY={"auto"} bg={"gray.50"} py={{base: 10, md: 16}}>
            <Container maxW={"4xl"}>
                <Box bg={"white"} borderRadius={"xl"} borderWidth={"1px"} borderColor={"gray.200"} p={{base: 6, md: 10}} boxShadow={"sm"}>
                    <Stack spacing={6}>
                        <Box>
                            <Text fontSize={"xs"} letterSpacing={"0.3em"} textTransform={"uppercase"} color={"gray.500"}>
                                SoundSpots
                            </Text>
                            <Heading size={"lg"} mt={2}>Privacy Policy</Heading>
                            <Text fontSize={"sm"} color={"gray.600"} mt={2}>
                                Effective date: 2026-01-05
                            </Text>
                        </Box>
                        <Divider/>
                        <Text fontSize={"sm"} color={"gray.700"}>
                            This Privacy Policy explains how the SoundSpots web application collects, uses, and protects
                            your information. It is provided to satisfy Google Cloud application branding and privacy
                            policy requirements.
                        </Text>
                        <Box>
                            <Heading size={"sm"} mb={2}>Information we collect</Heading>
                            <Stack spacing={2} fontSize={"sm"} color={"gray.700"}>
                                <Text>
                                    - Account data: name, email address, and profile information from your sign-in
                                    provider (Google).
                                </Text>
                                <Text>
                                    - App data: spaces, spots, sound file metadata, configuration settings, and related
                                    usage history you create in the app.
                                </Text>
                                <Text>
                                    - Usage data: logs required for security, troubleshooting, and performance.
                                </Text>
                            </Stack>
                        </Box>
                        <Box>
                            <Heading size={"sm"} mb={2}>How we use information</Heading>
                            <Stack spacing={2} fontSize={"sm"} color={"gray.700"}>
                                <Text>- To authenticate you and provide access to your organization.</Text>
                                <Text>- To store and manage your performances, sound spots, and related content.</Text>
                                <Text>- To maintain security, prevent abuse, and improve reliability.</Text>
                            </Stack>
                        </Box>
                        <Box>
                            <Heading size={"sm"} mb={2}>Data storage and sharing</Heading>
                            <Stack spacing={2} fontSize={"sm"} color={"gray.700"}>
                                <Text>
                                    SoundSpots stores data in Firebase (Google Cloud) services, including Firestore and
                                    Cloud Storage. We do not sell your personal data.
                                </Text>
                                <Text>
                                    Data is shared only with your organization and service providers required to run
                                    the app.
                                </Text>
                            </Stack>
                        </Box>
                        <Box>
                            <Heading size={"sm"} mb={2}>Data retention</Heading>
                            <Text fontSize={"sm"} color={"gray.700"}>
                                Data is retained while your account or organization uses the service. You can request
                                deletion of your account or organization data by contacting us.
                            </Text>
                        </Box>
                        <Box>
                            <Heading size={"sm"} mb={2}>Security</Heading>
                            <Text fontSize={"sm"} color={"gray.700"}>
                                We use standard security practices, including Firebase authentication and access
                                controls, to protect your data.
                            </Text>
                        </Box>
                        <Box>
                            <Heading size={"sm"} mb={2}>Your choices</Heading>
                            <Text fontSize={"sm"} color={"gray.700"}>
                                You can access, update, or request deletion of your data by contacting us.
                            </Text>
                        </Box>
                        <Box id={"data-deletion"}>
                            <Heading size={"sm"} mb={2}>Data deletion</Heading>
                            <Stack spacing={2} fontSize={"sm"} color={"gray.700"}>
                                <Text>
                                    You can request deletion of your account and associated data at any time by emailing{" "}
                                    <Link color={"blue.600"} href={"mailto:mail@jesperlyng.dk?subject=SoundSpots%20data%20deletion%20request"}>mail@jesperlyng.dk</Link>{" "}
                                    with the subject "SoundSpots data deletion request" from the email address tied to
                                    your account.
                                </Text>
                                <Text>
                                    On confirmation, the following will be deleted: your user record, profile
                                    information from sign-in providers (Google, Facebook), authored spaces, spots,
                                    sound files you uploaded, and any other content tied to your account.
                                </Text>
                                <Text>
                                    Requests are processed within 30 days. Some records may be retained for a limited
                                    period where required by law (for example billing records or security audit logs).
                                </Text>
                                <Text>
                                    If you signed in via Facebook and want to remove the SoundSpots app from your
                                    Facebook account, you can also do so from{" "}
                                    <Link color={"blue.600"} href={"https://www.facebook.com/settings?tab=applications"} isExternal>
                                        Facebook Settings → Apps and Websites
                                    </Link>. Removing the app there revokes our access to your Facebook profile;
                                    deletion of data already stored in SoundSpots still requires the email request
                                    above.
                                </Text>
                            </Stack>
                        </Box>
                        <Box>
                            <Heading size={"sm"} mb={2}>Contact</Heading>
                            <Text fontSize={"sm"} color={"gray.700"}>
                                For privacy questions, contact us at{" "}
                                <Link color={"blue.600"} href={"mailto:mail@jesperlyng.dk"}>mail@jesperlyng.dk</Link>.
                            </Text>
                        </Box>
                        <Text fontSize={"xs"} color={"gray.500"} pt={2}>
                            soundspots.dk is owned, maintained, and operated by Jesper Lyng ApS (Pvt. Ltd.), DK
                        </Text>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;